import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import withStyles from '../../decorators/withStyles';
import { SetZillowIsPct, SetZillowModel, SetZillowPathway, SetZillowPlaceType, SetZillowProb, 
    SetZillowVarType, SetZillowYear, SetZillowYrBuilt } from '../../actions';
import { capitalize } from '../../utils/utils';
import styles from './ZillowSettings.css';


class ZillowSettings extends Component {

  static propTypes = {
    forDownload: PropTypes.bool,
    zillowEndYr: PropTypes.string.isRequired,
    zillowIsPct: PropTypes.bool,
    zillowModel: PropTypes.string,
    zillowPathway: PropTypes.string,
    zillowPlaceType: PropTypes.string,
    zillowPlaceTypeOptions: PropTypes.array,
    zillowProb: PropTypes.string,
    zillowVarType: PropTypes.string,
    zillowYear: PropTypes.string,
    zillowYrBuilt: PropTypes.string,
  };

  static defaultProps = {
    forDownload: false,
  };

  handleIsPctChange = (value) => this.props.dispatch(SetZillowIsPct(value));
  handleModelChange = (value) => this.props.dispatch(SetZillowModel(value));
  handlePathwayChange = (value) => this.props.dispatch(SetZillowPathway(value));
  handlePlaceTypeChange = (value) => this.props.dispatch(SetZillowPlaceType(value));
  handleProbChange = (value) => this.props.dispatch(SetZillowProb(value));
  handleVarTypeChange = (value) => this.props.dispatch(SetZillowVarType(value));
  handleYearChange = (value) => this.props.dispatch(SetZillowYear(value));
  handleYrBuiltChange = (value) => this.props.dispatch(SetZillowYrBuilt(value));

  render() {
    const { forDownload, zillowEndYr, zillowIsPct, zillowModel, zillowPathway, zillowPlaceType,
            zillowPlaceTypeOptions, zillowProb, zillowVarType, zillowYear, zillowYrBuilt } = this.props;

    const varType = (
      <div className="zillow-setting" id="select-zillow-varType">
        <h3>Variable</h3>
        <label><input type="radio" onChange={this.handleVarTypeChange.bind(this, "Zestimate")} checked={zillowVarType === "Zestimate"} />Home value</label>
        <label><input type="radio" onChange={this.handleVarTypeChange.bind(this, "Count")} checked={zillowVarType === "Count"} />Home count</label>
        {zillowEndYr !== '2005' ? <label><input type="radio" onChange={this.handleVarTypeChange.bind(this, "Growth")} checked={zillowVarType === "Growth"} />New homes</label> : null}
        <p><b>New Homes</b> compares the number of new homes added within an area after 2009, relative to the number of homes known to have been built beforehand. (In most states, new home data run through 2017; in Alabama, Florida, Mississippi and New York they run through 2016.)</p>
      </div>      
    );

    const dataType = (
      <div className="zillow-setting" id="select-zillow-isPct">
        <h3>Scale</h3>
        <label><input type="radio" onChange={this.handleIsPctChange.bind(this, true)} checked={zillowIsPct} />Percentage at risk</label>
        <label><input type="radio" onChange={this.handleIsPctChange.bind(this, false)} checked={!zillowIsPct} />Total at risk</label>
      </div>
    );

    const placeType = () => {
      if (Object.keys(zillowPlaceTypeOptions).length > 2) {
        const locationOptions = zillowPlaceTypeOptions.map((option) => {
          const placeTypeLabel = capitalize(option.placeTypeLabel);
          return <label><input type="radio" onChange={this.handlePlaceTypeChange.bind(this, option.placeTypeValue)} checked={option.placeTypeValue === zillowPlaceType} />{placeTypeLabel}</label>;
        });
        return (
          <div className="zillow-setting" id="select-zillow-placeType">
            <h3>Location type</h3>
            {locationOptions}
          </div>
        );
      } else {
        return '';
      }
    };

    const builtYear = (
      <div className="zillow-setting" id="select-zillow-yrBuilt">
        <h3>Age of homes*</h3>
        <label><input type="radio" onChange={this.handleYrBuiltChange.bind(this, "All")} checked={zillowYrBuilt === "All"} />Homes built any year</label>
        <label><input type="radio" onChange={this.handleYrBuiltChange.bind(this, "Since2010")} checked={zillowYrBuilt === "Since2010"} />Homes built after 2009</label>
        <p>*Housing data are for years up to and including {zillowEndYr} and only include homes for which the year built is known.</p>
      </div>
    );

    const year = (
      <div className="zillow-setting" id="select-zillow-year">
        <h3>End year</h3>
        <label><input type="radio" onChange={this.handleYearChange.bind(this, "2050")} checked={zillowYear === "2050"} />2050</label>
        <label><input type="radio" onChange={this.handleYearChange.bind(this, "2100")} checked={zillowYear === "2100"} />2100</label>
      </div>
    );

    const pathway = (
      <div className="zillow-setting" id="select-zillow-pathway">
        <h3>Climate pollution scenario</h3>
        <p>The RCP scenarios are standard scenarios widely used by scientists and the Intergovernmental Panel on Climate Change.</p>
        <label><input type="radio" onChange={this.handlePathwayChange.bind(this, "RCP85")} checked={zillowPathway === "RCP85"} />Unchecked pollution (RCP 8.5)</label>
        <label><input type="radio" onChange={this.handlePathwayChange.bind(this, "RCP45")} checked={zillowPathway === "RCP45"} />Moderate carbon cuts (RCP 4.5)</label>
        <label><input type="radio" onChange={this.handlePathwayChange.bind(this, "RCP26")} checked={zillowPathway === "RCP26"} />Extreme carbon cuts (RCP 2.6)</label>
      </div>      
    );

    const model = (
      <div className="zillow-setting" id="select-zillow-model">
        <h3>Sea-level rise model</h3>
        <label><input type="radio" onChange={this.handleModelChange.bind(this, "DP16")} checked={zillowModel === "DP16"} />Kopp et al. (2017): With Antarctic dynamics</label>
        <label><input type="radio" onChange={this.handleModelChange.bind(this, "BK")} checked={zillowModel === "BK"} />Kopp et al. (2014): Probabilistic</label>
      </div>   
    );

    const probability = (
      <div className="zillow-setting" id="select-zillow-prob">
        <h3>Model sensitivity</h3>
        <label><input type="radio" onChange={this.handleProbChange.bind(this, "p05")} checked={zillowProb === "p05"} />5th percentile</label>
        <label><input type="radio" onChange={this.handleProbChange.bind(this, "p50")} checked={zillowProb === "p50"} />50th percentile (median)</label>
        <label><input type="radio" onChange={this.handleProbChange.bind(this, "p95")} checked={zillowProb === "p95"} />95th percentile</label>
      </div>
    );

    return (
      <div className="ZillowSettings" id="select-zillow-scenario">
        {forDownload ? null : varType}
        {forDownload ? null : dataType}
        {placeType()}
        {forDownload || zillowEndYr === '2005' ? null : builtYear}
        {forDownload ? null : year}
        {forDownload ? null : pathway}
        {model}
        {forDownload ? null : probability}
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  connect(() => {return {};}),
)(ZillowSettings);
